import React, { useState, useEffect, useRef } from 'react';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { getWarehouses } from '../../services/warehousesService';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const EntryGuideTraceability = () => {
  const [traceabilityData, setTraceabilityData] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  
  // Filtros
  const [entryGuideCode, setEntryGuideCode] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [guideType, setGuideType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Ref para evitar llamadas duplicadas
  const hasLoadedWarehouses = useRef(false);

  useEffect(() => {
    if (!hasLoadedWarehouses.current) {
      hasLoadedWarehouses.current = true;
      loadWarehouses();
    }
  }, []);

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses({ active_only: true });
      if (response.success) {
        setWarehouses(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar almacenes:', error);
    }
  };

  const loadTraceability = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (entryGuideCode) filters.entry_guide_code = entryGuideCode;
      if (selectedWarehouse) filters.warehouse_id = selectedWarehouse;
      if (guideType) filters.guide_type = guideType;
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;

      const response = await reportService.getEntryGuideTraceability(filters);
      
      if (response.success) {
        setTraceabilityData(response.data || []);
        if (response.data.length === 0) {
          setToast({
            isOpen: true,
            message: 'No se encontraron guías con los filtros seleccionados',
            type: 'info'
          });
        }
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar la trazabilidad',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar trazabilidad:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar la trazabilidad de guías',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (traceabilityData.length === 0) {
      setToast({
        isOpen: true,
        message: 'No hay datos para exportar',
        type: 'warning'
      });
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Trazabilidad Guías');

      // Estilos
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E78' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1F4E78' }
        },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      // Título
      worksheet.mergeCells('A1:F1');
      const titleRow = worksheet.getRow(1);
      titleRow.height = 30;
      titleRow.getCell(1).value = 'Reporte de Trazabilidad de Guías';
      titleRow.getCell(1).style = titleStyle;

      // Encabezados
      const headers = [
        'Código Guía',
        'Tipo',
        'Almacén',
        'Fecha Emisión',
        'Origen (OC/Doc)',
        'Items'
      ];

      const headerRow = worksheet.getRow(2);
      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.style = headerStyle;
      });
      headerRow.height = 25;

      // Datos
      let rowIndex = 3;
      traceabilityData.forEach((trace) => {
        const guide = trace.entry_guide;
        const origin = trace.origins.purchase_order 
          ? `OC: ${trace.origins.purchase_order.code}`
          : trace.origins.document
          ? `Doc: ${trace.origins.document.code}`
          : 'N/A';
        const itemsCount = trace.items.length;

        const row = worksheet.getRow(rowIndex);
        row.getCell(1).value = guide.code;
        row.getCell(2).value = guide.guide_type;
        row.getCell(3).value = guide.warehouse?.name || 'N/A';
        row.getCell(4).value = guide.issue_date ? formatDateLocaleLima(guide.issue_date) : 'N/A';
        row.getCell(5).value = origin;
        row.getCell(6).value = itemsCount;

        rowIndex++;
      });

      // Ajustar anchos de columna
      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Trazabilidad_Guias_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: 'Reporte exportado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el reporte',
        type: 'error'
      });
    }
  };

  const getGuideTypeLabel = (type) => {
    const labels = {
      'entrada': 'Entrada',
      'salida': 'Salida',
      'inicial': 'Inicial',
      'traspaso': 'Traspaso',
      'recepcion': 'Recepción',
    };
    return labels[type] || type;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Trazabilidad de Guías</h1>
            <p className="text-gray-600 mt-1">Rastrea qué documento u orden originó la guía</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportToExcel}
              disabled={traceabilityData.length === 0}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2"
            >
              <span>📥</span>
              Exportar a Excel
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código de Guía
              </label>
              <input
                type="text"
                value={entryGuideCode}
                onChange={(e) => setEntryGuideCode(e.target.value)}
                placeholder="Ej: GE-0001"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Almacén
              </label>
              <select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todos</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} - {warehouse.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Guía
              </label>
              <select
                value={guideType}
                onChange={(e) => setGuideType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todos</option>
                <option value="entrada">Entrada</option>
                <option value="salida">Salida</option>
                <option value="inicial">Inicial</option>
                <option value="traspaso">Traspaso</option>
                <option value="recepcion">Recepción</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={loadTraceability}
              disabled={loading}
              className="px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors"
            >
              {loading ? 'Cargando...' : 'Buscar'}
            </button>
          </div>
        </div>

        {/* Resultados */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-2 text-gray-600">Cargando trazabilidad...</p>
          </div>
        ) : traceabilityData.length > 0 ? (
          <div className="space-y-6">
            {traceabilityData.map((trace) => {
              const guide = trace.entry_guide;
              return (
                <div key={guide.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  {/* Información de la Guía */}
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{guide.code}</h3>
                        <p className="text-sm text-gray-600">
                          Tipo: {getGuideTypeLabel(guide.guide_type)} | 
                          Proveedor: {guide.supplier?.name || 'N/A'} | 
                          Almacén: {guide.warehouse?.name || 'N/A'} | 
                          Fecha: {guide.issue_date ? formatDateLocaleLima(guide.issue_date) : 'N/A'} | 
                          Total: S/ {parseFloat(guide.total_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        guide.status === 'issued' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {guide.status === 'issued' ? 'Emitida' : 'Cancelada'}
                      </span>
                    </div>
                  </div>

                  {/* Orígenes */}
                  {(trace.origins.purchase_order || trace.origins.document) && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Origen:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {trace.origins.purchase_order && (
                          <div className="bg-blue-50 p-3 rounded">
                            <p className="text-xs text-gray-600 mb-1">Orden de Compra:</p>
                            <p className="text-sm font-medium">{trace.origins.purchase_order.code}</p>
                            <p className="text-xs text-gray-500">
                              Proveedor: {trace.origins.purchase_order.supplier?.name || 'N/A'} | 
                              Fecha: {trace.origins.purchase_order.order_date ? formatDateLocaleLima(trace.origins.purchase_order.order_date) : 'N/A'} | 
                              Total: S/ {parseFloat(trace.origins.purchase_order.total_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                        {trace.origins.document && (
                          <div className="bg-purple-50 p-3 rounded">
                            <p className="text-xs text-gray-600 mb-1">Documento:</p>
                            <p className="text-sm font-medium">
                              {trace.origins.document.code} {trace.origins.document.series && trace.origins.document.number ? `(${trace.origins.document.series}-${trace.origins.document.number})` : ''}
                            </p>
                            <p className="text-xs text-gray-500">
                              Proveedor: {trace.origins.document.supplier?.name || 'N/A'} | 
                              Fecha: {trace.origins.document.issue_date ? formatDateLocaleLima(trace.origins.document.issue_date) : 'N/A'} | 
                              Total: S/ {parseFloat(trace.origins.document.total_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Items */}
                  {trace.items.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Items ({trace.items.length}):</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Producto/Servicio</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Proyecto</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Condición</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Cantidad</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Precio Unit.</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Total</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {trace.items.map((item) => (
                              <tr key={item.id}>
                                <td className="px-3 py-2 text-gray-600">
                                  {item.product ? `${item.product.sku} - ${item.product.name}` : '-'}
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {item.project ? `${item.project.code} - ${item.project.name}` : '-'}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                    item.condition === 'good' ? 'bg-green-100 text-green-800' :
                                    item.condition === 'damaged' ? 'bg-yellow-100 text-yellow-800' :
                                    item.condition === 'defective' ? 'bg-red-100 text-red-800' :
                                    item.condition === 'reusable' ? 'bg-blue-100 text-blue-800' :
                                    item.condition === 'non_reusable' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {item.condition === 'good' ? 'Bueno' :
                                     item.condition === 'damaged' ? 'Dañado' :
                                     item.condition === 'defective' ? 'Defectuoso' :
                                     item.condition === 'reusable' ? 'Reutilizable' :
                                     item.condition === 'non_reusable' ? 'No reutilizable' : item.condition}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right text-gray-900">{parseFloat(item.quantity || 0).toFixed(3)}</td>
                                <td className="px-3 py-2 text-right text-gray-900">
                                  S/ {parseFloat(item.unit_price || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                  S/ {parseFloat(item.total || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-500">No hay datos para mostrar. Usa los filtros para buscar guías.</p>
          </div>
        )}
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default EntryGuideTraceability;
